import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import {
  StatusBar,
  Modal,
  PrimaryButton,
  SecondaryButton,
  SnackbarStatus,
  Typography,
} from '@weareopenr/component-library';
import type { IMenuItem } from 'src/types/menu.types';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { downStreamExternalSystemIntegrationTypes, EntitySource, OptionGroupType } from 'src/constants';
import { createOptionGroup } from 'src/api/createOptionGroup';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { OptionGroupPricing } from '../OptionGroupPricing';
import { SetPublicationExternalSystems } from './PublicationChannels/SetPublicationExternalSystems';
import { OptionGroupDetails } from './OptionGroupDetails/OptionGroupDetails';
import { OptionGroupPriceSchemes } from './OptionGroupPriceSchemes';
import { OptionGroupType as OptionGroupTypeStep } from './OptionGroupType/OptionGroupType';
import { formatCreateOptionGroupRequest } from './utilities';
import { IOptionGroupValues } from './types';
import chefPlating from 'src/assets/images/chef-plating.png';
import styles from './CreateOptionGroupModal.module.scss';

interface IModalProps {
  open: boolean;
  onClose: () => void;
  item?: Pick<IMenuItem, 'itemId' | 'type'>;
  bundleRangeIds?: string[];
}

enum FlowSteps {
  TypeSelection = -1,
  Details = 0,
  PriceSchemes = 1,
  Pricing = 2,
  externalSystems = 3,
}

interface ICreateOptionGroupFormProps extends Omit<IModalProps, 'open'> {
  step: FlowSteps;
  setStep: Dispatch<SetStateAction<FlowSteps>>;
}

const getDefaultSource = (
  optionGroupType: OptionGroupType | undefined,
  bundleRangeIds: string[] = [],
): { ranges: string[]; source: EntitySource } => {
  if (optionGroupType === OptionGroupType.BUNDLE && bundleRangeIds.length > 0) {
    return {
      ranges: bundleRangeIds,
      source: EntitySource.RANGES,
    };
  }
  return {
    ranges: [],
    source: EntitySource.LIBRARY,
  };
};

const getDefaultValues = (
  optionGroupType: OptionGroupType | undefined,
  bundleRangeIds: string[] = [],
): IOptionGroupValues => ({
  name: '',
  consumer_name: '',
  description: '',
  plu: '',
  min_choices: 0,
  max_choices: 1,
  externalSystems: [],
  modifiers: [],
  price_scheme_ids: [],
  optionGroupType,
  ...getDefaultSource(optionGroupType, bundleRangeIds),
});

const CreateOptionGroupForm = ({ item, onClose, bundleRangeIds, step, setStep }: ICreateOptionGroupFormProps) => {
  const queryClient = useQueryClient();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { data: externalSystems } = useExternalSystems();

  const standalone = !item;
  const itemIds = item?.itemId ? [item.itemId] : [];

  const getDefaultOptionGroupType = (): OptionGroupType | undefined => {
    if (standalone) return undefined;
    return item.type === 'BUNDLE' ? OptionGroupType.BUNDLE : OptionGroupType.MODIFIER;
  };

  const defaultOptionGroupType = getDefaultOptionGroupType();
  const defaultValues = getDefaultValues(defaultOptionGroupType, bundleRangeIds);

  const useFormReturn = useForm<IOptionGroupValues>({ defaultValues, mode: 'onChange' });
  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    watch,
    trigger,
    setValue,
    reset,
  } = useFormReturn;

  const priceSchemeIds = useWatch({ control: useFormReturn.control, name: 'price_scheme_ids' }) || [];
  const selectedOptionGroupType = watch('optionGroupType');

  useEffect(() => {
    if (standalone && selectedOptionGroupType) {
      reset(getDefaultValues(selectedOptionGroupType, bundleRangeIds));
    }
  }, [selectedOptionGroupType, bundleRangeIds, reset, standalone]);

  const stepOffset = standalone ? 2 : 1;
  const shouldSkipExternalSystems = selectedOptionGroupType === OptionGroupType.BUNDLE;

  const getStep = useCallback(
    (currentStep: number, direction: 'next' | 'prev') => {
      const adjustment = direction === 'next' ? 1 : -1;
      const nextStep = currentStep + adjustment;

      // Skip ExternalSystems step for bundle groups
      const shouldSkip = nextStep === FlowSteps.externalSystems && shouldSkipExternalSystems;

      return shouldSkip ? nextStep + adjustment : nextStep;
    },
    [shouldSkipExternalSystems],
  );

  const finalStep = useMemo(() => {
    const hasDownstreamSystems = externalSystems?.some((sys) =>
      downStreamExternalSystemIntegrationTypes.includes(sys.integrationType),
    );

    if (shouldSkipExternalSystems || !hasDownstreamSystems) {
      return FlowSteps.Pricing;
    }
    return FlowSteps.externalSystems;
  }, [externalSystems, shouldSkipExternalSystems]);

  const isFirstStep = step === (standalone ? FlowSteps.TypeSelection : FlowSteps.Details);

  const nextStep = useCallback(async () => {
    const valid = await trigger();
    if (valid) setStep((prev) => getStep(prev, 'next'));
  }, [trigger, getStep]);

  const previousStep = useCallback(() => {
    setStep((prev) => getStep(prev, 'prev'));
  }, [getStep]);

  const submitHandler = useCallback(
    async (data: IOptionGroupValues) => {
      clearError();
      try {
        const payload = formatCreateOptionGroupRequest(data, itemIds, priceSchemeIds);
        await createOptionGroup(payload);

        await queryClient.invalidateQueries([QueryKeys.productSizes]);
        if (item?.itemId) {
          await queryClient.invalidateQueries([QueryKeys.item, item.itemId]);
        }

        onClose();
        showNotification({
          status: SnackbarStatus.SUCCESS,
          message: `You have successfully created ${data.name}`,
        });
      } catch (error) {
        console.error('Failed to create option group:', error);
        showNotification({
          status: SnackbarStatus.ERROR,
          message: error instanceof Error ? error.message : `Something went wrong whilst creating ${data.name}`,
        });
      }
    },
    [clearError, itemIds, queryClient, item?.itemId, onClose, showNotification, priceSchemeIds],
  );

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <Typography size="small" className={styles['step']}>
        Step {step + stepOffset} of {finalStep + stepOffset}
      </Typography>
      {step === FlowSteps.TypeSelection && <OptionGroupTypeStep useFormReturn={useFormReturn} />}
      {step === FlowSteps.Details && (
        <OptionGroupDetails useFormReturn={useFormReturn} optionGroupType={selectedOptionGroupType} />
      )}
      {step === FlowSteps.PriceSchemes && <OptionGroupPriceSchemes useFormReturn={useFormReturn} />}
      {step === FlowSteps.Pricing && <OptionGroupPricing formReturn={useFormReturn} />}
      {step === FlowSteps.externalSystems && (
        <SetPublicationExternalSystems
          externalSystems={externalSystems}
          selectedExternalSystems={watch('externalSystems')}
          setValue={setValue}
        />
      )}
      <ButtonContainer
        left={
          <SecondaryButton
            size="full-width"
            type="button"
            buttonStyle="outline"
            onClick={isFirstStep ? onClose : previousStep}
          >
            {isFirstStep ? 'Cancel' : 'Back'}
          </SecondaryButton>
        }
        right={
          <>
            {step === FlowSteps.TypeSelection && (
              <PrimaryButton size="full-width" type="button" onClick={nextStep} disabled={!selectedOptionGroupType}>
                Continue
              </PrimaryButton>
            )}
            {step !== FlowSteps.TypeSelection && step === finalStep && (
              <PrimaryButton loading={isSubmitting} size="full-width" type="submit" disabled={!isValid}>
                Create
              </PrimaryButton>
            )}
            {step !== FlowSteps.TypeSelection && step !== finalStep && (
              <PrimaryButton size="full-width" type="button" onClick={nextStep} disabled={!isValid}>
                Next
              </PrimaryButton>
            )}
          </>
        }
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};

export const CreateOptionGroupModal = ({ open, onClose, item, bundleRangeIds }: IModalProps) => {
  const initialStep = item ? FlowSteps.Details : FlowSteps.TypeSelection;
  const [step, setStep] = useState(initialStep);

  const handleClose = () => {
    setStep(initialStep);
    onClose();
  };

  return (
    <Modal
      closeModal={handleClose}
      open={open}
      image={step === FlowSteps.Details ? undefined : chefPlating}
      imageAlt="chef"
    >
      <CreateOptionGroupForm
        item={item}
        onClose={handleClose}
        bundleRangeIds={bundleRangeIds}
        step={step}
        setStep={setStep}
      />
    </Modal>
  );
};

export default CreateOptionGroupModal;
